import React from 'react'
import { Link } from 'gatsby'
import {
  Layout,
  Intro,
  Showcase,
  Header,
  SectionTitle,
} from '../../../../components'

import './style.scss'
import data from './data.json'

export default () => {
  return (
    <>
      <Header
        site={data.site}
        slug={data.slug + '/develop/'}
        state="develop"
        title={data.title}
      />
      <Layout
        site={data.site}
        slug={data.slug + '/develop/'}
        state="develop"
        title={data.title}
      >
        <Intro>
          <p>
            Text inputs are form inputs that gather plain text information from
            users. These classes are designed for{' '}
            <span class="inline__code">{'<input type="text">'}</span> elements
            or <span class="inline__code">{'<textarea>'}</span> elements. An
            associated label is required. Optional placeholder and caption
            elements can also be added.
          </p>
        </Intro>
        <SectionTitle>Variants</SectionTitle>
        <Showcase
          site={data.site}
          title="Text Input"
          text={
            <span>
              Use <span class="inline__code">c-text-input</span> when the
              expected input is a single line, such as a name or phone number.
            </span>
          }
          code={code.input}
        >
          <div>
            <input
              type="text"
              class="c-text-input"
              placeholder="e.g. Homer J. Simpson"
              id="text-id-1"
            />
          </div>
        </Showcase>
        <Showcase
          id="TextArea"
          site={data.site}
          title="Text Area"
          text={
            <span>
              Use the <span class="inline__code">c-textarea</span> field when
              the expected input is more than one line, such as a comment or
              contact request.
            </span>
          }
          code={code.textarea}
        >
          <div>
            <textarea
              name=""
              id="text-area"
              class="c-textarea"
              placeholder="Textarea placeholder"
            ></textarea>
          </div>
        </Showcase>
        <SectionTitle>States</SectionTitle>
        <p>
          In addition to the default state, both types of text inputs have
          hover, focus, change, blur and error states. The error state must be
          used with an{' '}
          <Link
            to={'/' + data.site + '/components/inline-error/design'}
            class="link"
          >
            input error
          </Link>{' '}
          component. Use{' '}
          <span className="inline__code">c-text-input--error</span> to apply the
          error state. An optional disabled state is also available. Use{' '}
          <span className="inline__code">c-text-input--disabled</span> to apply
          the disabled state.
        </p>

        <Showcase
          site={data.site}
          title="Text Input in Disabled State"
          text="Use the disabled state when another action must be completed before the user can interact with the input."
          code={code.disabled}
        >
          <div>
            <input
              type="text"
              class="c-text-input--disabled"
              placeholder="e.g. Homer J. Simpson"
              value="John Doe"
              disabled
              id="text-id-3"
            />
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Text Area in Disabled State"
          text="Use the disabled state when another action must be completed before the user can interact with the input."
          code={code.textAreaDisabled}
        >
          <div>
            <textarea
              name=""
              id="text-area"
              class="c-textarea c-textarea--disabled"
              placeholder="The quick brown fox jumps over the lazy dog."
            ></textarea>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Text Input in Error State"
          text={
            <p>
              A text input or text area in the error state must be used with an{' '}
              <Link
                to={'/' + data.site + '/components/inline-error/design/'}
                className="link"
              >
                input error
              </Link>{' '}
              component to guide the user in correcting the error.
            </p>
          }
          code={code.error}
        >
          <div>
            <input
              type="text"
              class="c-text-input c-text-input--error"
              placeholder="Hot tea!"
              id="text-input-1"
            />
            <span class="c-inline-error">
              <i class="icon icon--error c-inline-error__icon"></i>
              <span class="c-inline-error__content">
                Tell me something good.
              </span>
            </span>
          </div>
        </Showcase>
        <Showcase
          site={data.site}
          title="Text Area in Error State"
          text={
            <p>
              A text input or text area in the error state must be used with an{' '}
              <Link
                to={'/' + data.site + '/components/inline-error/design/'}
                className="link"
              >
                input error
              </Link>{' '}
              component to guide the user in correcting the error.
            </p>
          }
          code={code.textAreaError}
        >
          <div>
            <textarea
              name=""
              id="text-area"
              class="c-textarea c-textarea--error"
              placeholder="The quick brown fox jumps over the lazy dog."
            ></textarea>
            <span class="c-inline-error">
              <i class="icon icon--error c-inline-error__icon"></i>
              <span class="c-inline-error__content">
                Tell me something good.
              </span>
            </span>
          </div>
        </Showcase>
        <hr />
        <SectionTitle>Design Environment</SectionTitle>
        <p>
          Use <span class="inline__code">.l-margin-vertical--medium</span>{' '}
          between the text input and any components above or below it. Text
          inputs will fill 100% width of the parent container when standing
          alone. When inline with another component, they will fill 50% width.
          Use <span class="inline__code">.l-margin-horizontal--medium</span>{' '}
          between the text input and any inline components.
        </p>
        <hr />
        <SectionTitle>Microcopy</SectionTitle>
        <p>
          Placeholders should give the user an example or show the required
          format of the input. Never use a placeholder instead of a label, as
          the user will lose the prompt once they begin typing. Use sentence
          case with no end punctuation, unless otherwise dictated by the input
          format.
        </p>
      </Layout>
    </>
  )
}

export const code = {
  input: `<input type="text" class="c-text-input" placeholder="Text input placeholder" />
`,
  textarea: `<textarea name="" id="" class="c-textarea" placeholder="Textarea placeholder"></textarea>
`,
  success: `<input type="text" class="c-text-input" placeholder="Text input placeholder" value="John Doe" />
`,
  disabled: `<input type="text" class="c-text-input--disabled" placeholder="Text input placeholder" value="John Doe" disabled />
`,
  error: `<div>
  <input
    type="text"
    class="c-text-input c-text-input--error"
    placeholder="e.g. Homer J. Simpson"
    id="text-input-1"
  />
  <span class="c-inline-error">
    <i class="icon icon--error c-inline-error__icon"></i>
    <span class="c-inline-error__content">
      Tell me something good.
    </span>
  </span>
</div>`,
  textAreaDisabled: `<textarea
  name=""
  id="text-area"
  class="c-textarea c-textarea--disabled"
  placeholder="The quick brown fox jumps over the lazy dog."
></textarea>`,
  textAreaError: `<div>
<textarea
  name=""
  id="text-area"
  class="c-textarea c-textarea--error"
  placeholder="The quick brown fox jumps over the lazy dog."
></textarea>
<span class="c-inline-error">
  <i class="icon icon--error c-inline-error__icon"></i>
  <span class="c-inline-error__content">
    Tell me something good.
  </span>
</span>
</div>`,
}
